import React, { useState, useEffect, useMemo, useRef } from "react";
import Vapi from "@vapi-ai/web";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { MdPhone } from "@react-icons/all-files/md/MdPhone";
import { MdCallEnd } from "@react-icons/all-files/md/MdCallEnd";
import {
  isBrowser,
  isMobileOnly,
  isSmartTV,
  isTablet,
  isWearable,
} from "react-device-detect";
import NPSModal from "../../components/NPSModal";
import LoadingOverlay from "../../components/LoadingOverlay";
import MobileAlert from "../../components/MobileAlert";
import PreparationScreen from "../../components/PreparationScreen";
import { useCallTranslations } from "../../i18n/hooks/useCallTranslations";
// Importar archivos de audio con voz real

import {
  Overlay,
  Card,
  Avatar,
  Greeting,
  StatusTitle,
  StatusSub,
  ButtonRow,
  CallButton,
  HangupButton,
  CallTimer,
  TimerDisplay,
  ProgressBarContainer,
  ProgressBar,
  CallStatusMessage,
  AvatarImage,
  ErrorText,
} from "./styled";
import { INTERVIEW_AI_SOCKET_URL, INTERVIEW_AI_URL } from "../../system/keys";

import { useParams } from "react-router-dom";

// Usando react-icons directamente como en la documentación oficial

function getDevice() {
  if (isMobileOnly) return "mobile";
  if (isTablet) return "tableta";
  if (isBrowser) return "desktop";
  if (isSmartTV) return "smart";
  if (isWearable) return "wearable";

  return "desktop";
}

interface VapiWidgetProps {
  apiKey: string;
  assistantId: string;
  config?: Record<string, unknown>;
  assistantData?: any;
}

const VapiWidget: React.FC<VapiWidgetProps> = ({
  assistantId,
  assistantData,
}) => {
  // Hook de traducciones
  const { translations } = useCallTranslations({ assistantData });
  const [vapi, setVapi] = useState<Vapi | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [isCalling, setIsCalling] = useState(false);
  const [localAssistantStatus, setLocalAssistantStatus] = useState(
    assistantData?.status
  );

  const [showNPSModal, setShowNPSModal] = useState(false);

  const [timeRemaining, setTimeRemaining] = useState(0);
  const [isWebSocketConnected, setIsWebSocketConnected] = useState(false);
  const [websocket, setWebsocket] = useState<WebSocket | null>(null);
  const [isLoadingCall, setIsLoadingCall] = useState(false);
  const [showMobileAlert, setShowMobileAlert] = useState(false);
  const [selectedMicrophoneId, setSelectedMicrophoneId] = useState<
    string | null
  >(null);
  const [selectedSpeakerId, setSelectedSpeakerId] = useState<string | null>(
    null
  );
  const microphoneIdRef = useRef<string | null>(null);

  // Memoizar apiKey para evitar re-renders innecesarios
  const memoizedApiKey = useMemo(
    () => process.env.REACT_APP_VAPI_API_KEY || "",
    []
  );

  const maxDurationMinutes = Math.round(
    (assistantData?.maxDurationSeconds || 15 * 60) / 60
  );
  const maxDurationSeconds = maxDurationMinutes * 60;
  const location = useLocation();

  // Detect if we are in /call route
  const isCallPage = location.pathname.startsWith("/call");

  // Interceptar getUserMedia ANTES de inicializar Vapi para asegurar que esté activo desde el principio
  useEffect(() => {
    const originalGetUserMedia = navigator.mediaDevices.getUserMedia.bind(
      navigator.mediaDevices
    );

    // Interceptar RTCPeerConnection.addTrack para asegurar que todos los tracks usen nuestro micrófono
    if (window.RTCPeerConnection) {
      const OriginalRTCPeerConnection = window.RTCPeerConnection;
      // Guardar el original para poder restaurarlo
      (window as any).__OriginalRTCPeerConnection = OriginalRTCPeerConnection;

      (window as any).RTCPeerConnection = function (
        this: RTCPeerConnection,
        configuration?: RTCConfiguration
      ) {
        const pc = new OriginalRTCPeerConnection(configuration);

        // Interceptar addTrack para reemplazar tracks con el micrófono incorrecto
        const originalAddTrack = pc.addTrack.bind(pc);
        (pc as any).addTrack = async function (
          track: MediaStreamTrack,
          ...streams: MediaStream[]
        ) {
          const settings = track.getSettings();
          const currentMicId = microphoneIdRef.current;

          // Si es un track de audio y no está usando el micrófono correcto, reemplazarlo
          if (
            currentMicId &&
            track.kind === "audio" &&
            settings.deviceId &&
            settings.deviceId !== currentMicId
          ) {
            try {
              // Obtener un nuevo track con el micrófono correcto usando nuestro interceptor
              const newStream = await originalGetUserMedia({
                audio: { deviceId: { exact: currentMicId } },
              });
              const newTrack = newStream.getAudioTracks()[0];

              if (newTrack) {
                const newSettings = newTrack.getSettings();

                // Detener el track viejo
                track.stop();

                // Agregar el nuevo track en lugar del viejo
                const result = originalAddTrack.call(pc, newTrack, ...streams);

                // Detener los otros tracks del stream temporal que creamos (solo necesitábamos el track)
                newStream.getTracks().forEach((t) => {
                  if (t !== newTrack) t.stop();
                });

                return result;
              }
            } catch (error) {
              console.error("Error reemplazando track:", error);
            }
          }

          // Si el track es correcto o no es de audio, agregarlo normalmente
          return originalAddTrack.call(pc, track, ...streams);
        };

        // Interceptar también addStream (método legacy pero aún usado por algunos)
        if ((pc as any).addStream) {
          const originalAddStream = (pc as any).addStream.bind(pc);
          (pc as any).addStream = async function (stream: MediaStream) {
            const currentMicId = microphoneIdRef.current;
            const audioTracks = stream.getAudioTracks();

            if (audioTracks.length > 0 && currentMicId) {
              const track = audioTracks[0];
              const settings = track.getSettings();

              if (settings.deviceId && settings.deviceId !== currentMicId) {
                try {
                  const newStream = await originalGetUserMedia({
                    audio: { deviceId: { exact: currentMicId } },
                  });
                  stream.getTracks().forEach((t) => t.stop());
                  return originalAddStream.call(pc, newStream);
                } catch (error) {
                  console.error("Error reemplazando stream:", error);
                }
              }
            }

            return originalAddStream.call(pc, stream);
          };
        }

        return pc;
      } as any;

      // Copiar propiedades estáticas
      Object.setPrototypeOf(
        (window as any).RTCPeerConnection,
        OriginalRTCPeerConnection
      );
      Object.getOwnPropertyNames(OriginalRTCPeerConnection).forEach((name) => {
        if (name !== "prototype" && name !== "length" && name !== "name") {
          (window as any).RTCPeerConnection[name] = (
            OriginalRTCPeerConnection as any
          )[name];
        }
      });
    }

    const getUserMediaInterceptor = async (
      constraints: MediaStreamConstraints
    ) => {
      const currentMicId = microphoneIdRef.current;

      if (currentMicId && constraints.audio) {
        let audioConstraints: any;

        if (typeof constraints.audio === "boolean") {
          audioConstraints = { deviceId: { exact: currentMicId } };
        } else {
          const existingConstraints = constraints.audio as any;
          audioConstraints = {
            ...(existingConstraints.echoCancellation !== undefined && {
              echoCancellation: existingConstraints.echoCancellation,
            }),
            ...(existingConstraints.noiseSuppression !== undefined && {
              noiseSuppression: existingConstraints.noiseSuppression,
            }),
            ...(existingConstraints.autoGainControl !== undefined && {
              autoGainControl: existingConstraints.autoGainControl,
            }),
            ...(existingConstraints.sampleRate !== undefined && {
              sampleRate: existingConstraints.sampleRate,
            }),
            ...(existingConstraints.sampleSize !== undefined && {
              sampleSize: existingConstraints.sampleSize,
            }),
            ...(existingConstraints.channelCount !== undefined && {
              channelCount: existingConstraints.channelCount,
            }),
            ...(existingConstraints.latency !== undefined && {
              latency: existingConstraints.latency,
            }),
            deviceId: { exact: currentMicId },
          };
        }

        try {
          let stream: MediaStream;
          try {
            stream = await originalGetUserMedia({
              ...constraints,
              audio: audioConstraints,
            });
          } catch (exactError) {
            const fallbackConstraints = {
              ...audioConstraints,
              deviceId: { ideal: currentMicId } as any,
            };
            try {
              stream = await originalGetUserMedia({
                ...constraints,
                audio: fallbackConstraints,
              });
            } catch (idealError) {
              const minimalConstraints: MediaTrackConstraints = {
                echoCancellation: audioConstraints.echoCancellation ?? true,
                noiseSuppression: audioConstraints.noiseSuppression ?? true,
                autoGainControl: audioConstraints.autoGainControl ?? true,
              };
              stream = await originalGetUserMedia({
                ...constraints,
                audio: minimalConstraints,
              });
            }
          }

          const audioTracks = stream.getAudioTracks();
          if (audioTracks.length > 0) {
            let track = audioTracks[0];
            let trackSettings = track.getSettings();

            if (
              trackSettings.deviceId &&
              trackSettings.deviceId !== currentMicId
            ) {
              track.stop();
              stream.getTracks().forEach((t) => t.stop());

              try {
                const correctStream = await originalGetUserMedia({
                  video: constraints.video || false,
                  audio: { deviceId: { exact: currentMicId } },
                });
                const correctTracks = correctStream.getAudioTracks();
                if (correctTracks.length > 0) {
                  const correctTrackSettings = correctTracks[0].getSettings();
                  if (correctTrackSettings.deviceId === currentMicId) {
                    stream = correctStream;
                    track = correctTracks[0];
                    trackSettings = correctTrackSettings;
                  } else {
                    throw new Error(
                      "Dispositivo incorrecto después del reintento"
                    );
                  }
                } else {
                  throw new Error("No se encontraron tracks");
                }
              } catch (retryError) {
                throw retryError;
              }
            }

            if (!track.enabled) {
              track.enabled = true;
            }
          }

          return stream;
        } catch (error) {
          try {
            const lastResortConstraints: MediaStreamConstraints = {
              video: constraints.video || false,
              audio: {
                deviceId: { ideal: currentMicId },
                echoCancellation: true,
                noiseSuppression: true,
                autoGainControl: true,
              },
            };
            return await originalGetUserMedia(lastResortConstraints);
          } catch (finalError) {
            return originalGetUserMedia(constraints);
          }
        }
      }

      return originalGetUserMedia(constraints);
    };

    navigator.mediaDevices.getUserMedia = getUserMediaInterceptor;

    // Interceptar también MediaStream.getAudioTracks para detectar tracks incorrectos
    // (solo logging, no reemplazar automáticamente para evitar loops infinitos)
    const originalGetAudioTracks = MediaStream.prototype.getAudioTracks;
    const replacedStreams = new WeakSet<MediaStream>(); // Track streams que ya hemos intentado reemplazar

    MediaStream.prototype.getAudioTracks = function () {
      const tracks = originalGetAudioTracks.call(this);
      const micId = microphoneIdRef.current;

      if (micId && tracks.length > 0 && !replacedStreams.has(this)) {
        tracks.forEach((track, idx) => {
          const settings = track.getSettings();
          if (
            settings.deviceId &&
            settings.deviceId !== micId &&
            track.kind === "audio"
          ) {
            // Marcar este stream como procesado para evitar loops
            replacedStreams.add(this);
          }
        });
      }

      return tracks;
    };

    // Interceptar también MediaStream.getTracks para detectar todos los tracks (audio y video)
    const originalGetTracks = MediaStream.prototype.getTracks;
    MediaStream.prototype.getTracks = function () {
      return originalGetTracks.call(this);
    };

    return () => {
      navigator.mediaDevices.getUserMedia = originalGetUserMedia;
      MediaStream.prototype.getAudioTracks = originalGetAudioTracks;
      if (window.RTCPeerConnection) {
        // Restaurar RTCPeerConnection original si existe
        const OriginalRTCPeerConnection = (window as any)
          .__OriginalRTCPeerConnection;
        if (OriginalRTCPeerConnection) {
          window.RTCPeerConnection = OriginalRTCPeerConnection;
        }
      }
    };
  }, []); // Ejecutar solo una vez al montar el componente

  useEffect(() => {
    const vapiInstance = new Vapi(memoizedApiKey);
    setVapi(vapiInstance);

    // Event listeners
    vapiInstance.on("call-start", () => {
      setIsConnected(true);
      setIsLoadingCall(true);
    });
    vapiInstance.on("call-end", () => {
      setIsConnected(false);
      setIsSpeaking(false);
      setIsLoadingCall(false);
      updateAssistantStatus(assistantId, {
        status: "finished",
        updatedAt: Date.now(),
      });
      setLocalAssistantStatus("finished");
    });
    vapiInstance.on("speech-start", () => {
      setIsSpeaking(true);
      setIsLoadingCall(false);
    });
    vapiInstance.on("speech-end", () => {
      setIsSpeaking(false);
    });
    vapiInstance.on("error", (error) => {
      console.error("Vapi error:", error);
      if (
        (error.message && error.message.includes("Meeting has ended")) ||
        (error.message && error.message.includes("ejection"))
      ) {
        setIsConnected(false);
        setIsSpeaking(false);
        setIsLoadingCall(false);
        updateAssistantStatus(assistantId, {
          status: "finished",
          updatedAt: Date.now(),
        });
        setLocalAssistantStatus("finished");
      }
    });

    return () => {
      vapiInstance?.stop();
    };
  }, [memoizedApiKey, assistantId]);

  // Actualizar ref cuando cambie el micrófono seleccionado
  useEffect(() => {
    microphoneIdRef.current = selectedMicrophoneId;
  }, [selectedMicrophoneId]);

  useEffect(() => {
    if (!isConnected) setIsCalling(false);
  }, [isConnected]);

  // Configurar altavoz en elementos de audio creados por Vapi
  useEffect(() => {
    if (!selectedSpeakerId || !("setSinkId" in HTMLAudioElement.prototype)) {
      return;
    }

    const speakerId = selectedSpeakerId;
    const configuredElements = new WeakSet<HTMLAudioElement>();

    // Interceptar setSinkId para prevenir que Vapi cambie el dispositivo
    const originalSetSinkId = (HTMLAudioElement.prototype as any).setSinkId;
    if (originalSetSinkId) {
      (HTMLAudioElement.prototype as any).setSinkId = function (
        sinkId: string
      ) {
        // Si este elemento ya fue configurado con nuestro dispositivo, forzar nuestro dispositivo
        if (configuredElements.has(this)) {
          return originalSetSinkId.call(this, speakerId);
        }
        // Permitir la configuración inicial, pero luego interceptar
        return originalSetSinkId.call(this, sinkId);
      };
    }

    const configureSpeaker = (audio: HTMLAudioElement) => {
      // Evitar configurar el mismo elemento múltiples veces
      if (configuredElements.has(audio)) {
        return;
      }

      try {
        // Configurar el dispositivo
        (audio as any)
          .setSinkId(speakerId)
          .then(() => {
            configuredElements.add(audio);
          })
          .catch(() => {
            // Si falla, intentar de nuevo después de un breve delay
            setTimeout(() => {
              if (!configuredElements.has(audio)) {
                (audio as any)
                  .setSinkId(speakerId)
                  .then(() => {
                    configuredElements.add(audio);
                  })
                  .catch(() => {
                    // Ignorar si falla de nuevo
                  });
              }
            }, 200);
          });
      } catch (error) {
        // Ignorar errores
      }
    };

    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        // Detectar nuevos elementos de audio
        mutation.addedNodes.forEach((node) => {
          if (node.nodeName === "AUDIO") {
            configureSpeaker(node as HTMLAudioElement);
          } else if ((node as Element).querySelectorAll) {
            (node as Element).querySelectorAll("audio").forEach((audio) => {
              configureSpeaker(audio as HTMLAudioElement);
            });
          }
        });
      });
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });

    // Configurar elementos existentes
    document.querySelectorAll("audio").forEach((audio) => {
      configureSpeaker(audio as HTMLAudioElement);
    });

    return () => {
      observer.disconnect();
      // Restaurar el método original
      if (originalSetSinkId) {
        (HTMLAudioElement.prototype as any).setSinkId = originalSetSinkId;
      }
    };
  }, [selectedSpeakerId]);

  // Desactivar estado de cargando después de 5 segundos como respaldo
  useEffect(() => {
    if (isLoadingCall) {
      const timeout = setTimeout(() => {
        setIsLoadingCall(false);
      }, 5000);
      return () => clearTimeout(timeout);
    }
  }, [isLoadingCall]);

  // Mostrar alerta permanente para dispositivos móviles
  useEffect(() => {
    if (isMobileOnly) {
      setShowMobileAlert(true);
    }
  }, []);

  // Mostrar modal de NPS cuando la llamada termine
  useEffect(() => {
    if (
      (localAssistantStatus === "finished" ||
        localAssistantStatus === "interrupted") &&
      !showNPSModal &&
      !assistantData?.hasFeedback
    ) {
      setShowNPSModal(true);
    }
  }, [localAssistantStatus, assistantData?.hasFeedback]);

  useEffect(() => {
    let interval: NodeJS.Timeout;

    if (isConnected && timeRemaining > 0) {
      interval = setInterval(() => {
        setTimeRemaining((prev) => {
          if (prev <= 1) return 0;
          return prev - 1;
        });
      }, 1000);
    }

    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [isConnected, timeRemaining, maxDurationSeconds]);

  // Conexión WebSocket
  useEffect(() => {
    if (!isConnected) return;

    const callId = sessionStorage.getItem(`callId_${assistantId}`);
    if (!callId) return;

    const wsUrl = `${INTERVIEW_AI_SOCKET_URL}/api/v1/call/${callId}/in-progress`;
    const ws = new WebSocket(wsUrl);

    // Eventos del WebSocket
    ws.onopen = () => {
      setIsWebSocketConnected(true);
      setWebsocket(ws);

      ws.send(JSON.stringify({ type: "call-in-progress" }));
    };

    ws.onclose = () => {
      setIsWebSocketConnected(false);
      setWebsocket(null);
    };

    ws.onerror = (error) => {
      console.error("Error en WebSocket:", error);
      setIsWebSocketConnected(false);
      setWebsocket(null);
    };

    ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);

        if (data.type === "call-terminated") {
          if (vapi) {
            endCall();
            setLocalAssistantStatus("finished");
          }
        }
      } catch (error) {
        console.error("Error parseando mensaje del WebSocket:", error);
      }
    };

    // Notificar al backend cuando la llamada finalice exitosamente
    const handleCallEnd = () => {
      if (ws && ws.readyState === WebSocket.OPEN) {
        ws.send(JSON.stringify({ type: "call-ended-successfully" }));
        ws.close();
      }
    };

    // Escuchar el evento de finalización de llamada
    vapi?.on("call-end", handleCallEnd);

    // Cleanup al desmontar
    return () => {
      if (ws) {
        ws.close();
      }
      vapi?.off("call-end", handleCallEnd);
    };
  }, [isConnected, assistantId, vapi]);

  // Enviar heartbeat cada 30 segundos
  useEffect(() => {
    if (!isWebSocketConnected || !isConnected) return;

    const heartbeatInterval = setInterval(() => {
      const callId = sessionStorage.getItem(`callId_${assistantId}`);
      if (callId && websocket && websocket.readyState === WebSocket.OPEN) {
        websocket.send(JSON.stringify({ type: "heartbeat" }));
      }
    }, 30000);

    return () => clearInterval(heartbeatInterval);
  }, [isWebSocketConnected, isConnected, assistantId, websocket]);

  const setCallIdWithStorage = (newCallId: string) => {
    sessionStorage.setItem(`callId_${assistantId}`, newCallId);
  };

  const clearCallIdFromStorage = () => {
    sessionStorage.removeItem(`callId_${assistantId}`);
  };

  const formatTime = (seconds: number): string => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes.toString().padStart(2, "0")}:${remainingSeconds
      .toString()
      .padStart(2, "0")}`;
  };

  const updateAssistantStatus = (assistantId: string, body: any) => {
    return axios
      .patch(`${INTERVIEW_AI_URL}/api/v1/assistants/${assistantId}`, body, {
        headers: {
          accept: "application/json",
          "Content-Type": "application/json",
        },
      })
      .catch((err) => {
        console.error("Error actualizando status:", err);
      });
  };

  const notifyCallStarted = (callId: string) => {
    return axios
      .post(
        `${INTERVIEW_AI_URL}/api/v1/call-started`,
        {
          callId: callId,
          enabled_webhook: true,
          device: getDevice(),
        },
        {
          headers: {
            accept: "application/json",
            "Content-Type": "application/json",
          },
        }
      )
      .catch((err) => {
        console.error("Error notificando inicio de llamada:", err);
      });
  };

  const startCall = async (deviceConfig?: {
    microphoneId?: string;
    speakerId?: string;
  }) => {
    if (vapi) {
      if (deviceConfig?.microphoneId) {
        microphoneIdRef.current = deviceConfig.microphoneId;
        setSelectedMicrophoneId(deviceConfig.microphoneId);
      }

      if (deviceConfig?.speakerId) {
        setSelectedSpeakerId(deviceConfig.speakerId);
      }

      setIsCalling(true);
      setIsLoadingCall(true);
      setTimeRemaining(maxDurationSeconds);
      updateAssistantStatus(assistantId, {
        status: "in process",
        updatedAt: Date.now(),
      });

      try {
        if (!microphoneIdRef.current && deviceConfig?.microphoneId) {
          microphoneIdRef.current = deviceConfig.microphoneId;
        }

        const call = await vapi.start(assistantId);
        if (call && call.id) {
          setCallIdWithStorage(call.id);
          notifyCallStarted(call.id);
        }
      } catch (error) {
        console.error("Error starting call:", error);
        setIsLoadingCall(false);
        setIsCalling(false);
      }
    }
  };

  const endCall = () => {
    if (vapi) {
      updateAssistantStatus(assistantId, {
        status: "finished",
        updatedAt: Date.now(),
      });
      setLocalAssistantStatus("finished");
      setTimeRemaining(maxDurationSeconds);
      setIsLoadingCall(false);
      setIsConnected(false);
      setIsCalling(false);
      vapi.stop();
    }
  };

  if (isCallPage) {
    const name = assistantData?.candidateData?.fullname;
    const assistantStatus = localAssistantStatus || assistantData?.status;
    let callStatusMessage = null;
    let canCall = true;
    if (assistantStatus === "in process") {
      callStatusMessage = translations.status.callInProgress;
      canCall = false;
    } else if (
      assistantStatus === "finished" ||
      assistantStatus === "interrupted"
    ) {
      callStatusMessage =
        assistantData?.source === "academy"
          ? translations.status.callFinishedAcademy
          : translations.status.callFinished;
      canCall = false;
    }

    return (
      <Overlay>
        <Card>
          {/* Mensaje de saludo */}
          {name && <Greeting>{translations.greeting(name)}</Greeting>}
          {/* Estado e avatar */}
          <Avatar isSpeaking={isSpeaking}>
            <AvatarImage
              src="https://firebasestorage.googleapis.com/v0/b/lapieza-production.appspot.com/o/EmpresasATS%2Fhome%2FTodo%20desde%20un%20solo%20lugar.png?alt=media&token=45271423-c588-4df0-ad95-ab43a9f70321"
              alt="Assistant"
            />
          </Avatar>
          {/* Timer y barra de progreso durante la llamada */}
          {isConnected && (
            <CallTimer>
              <TimerDisplay>{formatTime(timeRemaining)}</TimerDisplay>
              <ProgressBarContainer>
                <ProgressBar
                  progress={(timeRemaining / maxDurationSeconds) * 100}
                />
              </ProgressBarContainer>
            </CallTimer>
          )}

          <StatusTitle>
            {isLoadingCall
              ? translations.connecting
              : isSpeaking
              ? translations.status.assistantSpeaking
              : isConnected
              ? translations.status.listening
              : assistantStatus === "finished" ||
                assistantStatus === "interrupted"
              ? ""
              : translations.status.readyToCall}
          </StatusTitle>
          <StatusSub>
            {isConnected
              ? translations.status.inCall
              : translations.status.disconnected}
          </StatusSub>
          {/* Mensaje de status de llamada si aplica */}
          {callStatusMessage && (
            <CallStatusMessage>{callStatusMessage}</CallStatusMessage>
          )}

          {/* Botón de llamada o colgar */}
          <ButtonRow>
            {!isConnected ? (
              <CallButton
                onClick={() =>
                  startCall({
                    microphoneId: selectedMicrophoneId || undefined,
                    speakerId: selectedSpeakerId || undefined,
                  })
                }
                title={translations.buttons.startCall}
                disabled={isCalling || !canCall}
              >
                <MdPhone style={{ fontSize: "32px", color: "white" }} />
              </CallButton>
            ) : (
              <HangupButton
                onClick={endCall}
                title={translations.buttons.endCall}
              >
                <MdCallEnd style={{ fontSize: "32px", color: "white" }} />
              </HangupButton>
            )}
          </ButtonRow>
        </Card>

        {!isConnected && (
          <PreparationScreen
            localAssistantStatus={localAssistantStatus}
            assistantData={assistantData}
            translations={translations}
            assistantId={assistantId}
            startCall={startCall}
            onMicrophoneChange={(microphoneId) => {
              microphoneIdRef.current = microphoneId;
              setSelectedMicrophoneId(microphoneId);
            }}
            onSpeakerChange={(speakerId) => {
              setSelectedSpeakerId(speakerId);
            }}
          />
        )}

        {/* Modal de NPS */}
        <NPSModal
          isOpen={showNPSModal}
          onClose={() => {
            setShowNPSModal(false);
            clearCallIdFromStorage();
          }}
          assistantId={assistantId}
          callId={sessionStorage.getItem(`callId_${assistantId}`) || null}
          hasFeedback={assistantData?.hasFeedback}
          translations={translations}
        />

        {/* Pantalla de do completa */}
        <LoadingOverlay
          isVisible={isLoadingCall}
          title={translations.connecting}
          subtitle={translations.connectingSubtitle}
        />

        {/* Alerta permanente para dispositivos móviles */}
        <MobileAlert
          isVisible={showMobileAlert}
          translations={translations.mobileAlert}
          assistantSource={assistantData?.source}
        />
      </Overlay>
    );
  }

  // Widget flotante para otros casos (no tocado en esta migración)
  return null;
};

const CallPage: React.FC = () => {
  const { id: rawId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [exists, setExists] = useState<null | boolean>(null);
  const [loading, setLoading] = useState(true);
  const [assistantData, setAssistantData] = useState<any>(null);

  // API Key para el componente CallPage
  const apiKey = process.env.REACT_APP_VAPI_API_KEY || "";

  // Limpiar el ID removiendo parámetros de Gmail y otros
  const cleanAssistantId = (id: string) => {
    // Remover parámetros que empiezan con & o ?
    const cleanId = id.split("&")[0].split("?")[0];
    return cleanId;
  };

  const id = rawId ? cleanAssistantId(rawId) : null;

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    axios
      .get(`${INTERVIEW_AI_URL}/api/v1/assistants/${id}`, {
        headers: { accept: "application/json" },
      })
      .then((res) => {
        setExists(true);
        setAssistantData(res.data);
      })
      .catch(() => {
        setExists(false);
        setAssistantData(null);
      })
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) {
    return <LoadingOverlay isVisible={true} title="" subtitle="" />;
  }
  if (!exists) {
    navigate("/404");
    return null;
  }
  if (!id || !apiKey) {
    return (
      <ErrorText>
        Faltan parámetros en la URL o la API Key no está configurada.
      </ErrorText>
    );
  }

  return (
    <VapiWidget
      apiKey={apiKey}
      assistantId={id}
      assistantData={assistantData}
    />
  );
};

export default CallPage;
